import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { tyreSurveyAPI, vehiclesAPI, trailersAPI } from '../services/api';
import { toast } from 'sonner';
import { 
  CircleDot, 
  Plus, 
  Loader2, 
  Calendar,
  Truck,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  Edit,
  Trash2,
  ClipboardList,
  X,
  Eye,
  Download,
  Upload,
  FileText,
  Paperclip
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const downloadFile = async (url) => {
  try {
    const response = await axios.get(url, { responseType: 'blob' });
    const disposition = response.headers['content-disposition'];
    let filename = 'download';
    if (disposition) {
      const match = disposition.match(/filename=([^;]+)/);
      if (match) filename = match[1].replace(/"/g, '');
    }
    const blob = new Blob([response.data]);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  } catch (e) {
    toast.error('Download failed');
  }
};

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const formatMonth = (monthStr) => {
  if (!monthStr) return '';
  const [year, month] = monthStr.split('-');
  const date = new Date(year, month - 1);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
};

// Setup Dialog Component
const SetupDialog = ({ open, onClose, onSubmit, setup, vehicles, trailers }) => {
  const [formData, setFormData] = useState({
    month: getCurrentMonth(),
    truck_id: '',
    trailer_id: '',
    truck_tyre_count: 6,
    trailer_tyre_count: 0,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (setup) {
      setFormData({
        month: setup.month || getCurrentMonth(),
        truck_id: setup.truck_id || '',
        trailer_id: setup.trailer_id || '',
        truck_tyre_count: setup.truck_tyre_count || 6,
        trailer_tyre_count: setup.trailer_tyre_count || 0,
      });
    } else {
      setFormData({
        month: getCurrentMonth(),
        truck_id: '',
        trailer_id: '',
        truck_tyre_count: 6,
        trailer_tyre_count: 0,
      });
    }
  }, [setup, open]);

  const handleSubmit = async () => {
    if (!formData.truck_id) {
      toast.error('Please select a truck');
      return;
    }
    setLoading(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error saving setup:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter out trailers from vehicles list (trucks only)
  const trucks = vehicles.filter(v => 
    !v.vehicle_type?.toLowerCase().includes('trailer')
  );
  
  // Get trailers from both sources
  const allTrailers = [
    ...trailers,
    ...vehicles.filter(v => v.vehicle_type?.toLowerCase().includes('trailer'))
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{setup ? 'Edit Setup' : 'New Monthly Setup'}</DialogTitle>
          <DialogDescription>
            Configure the truck-trailer pairing and tyre counts for the month
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="month">Month</Label>
            <Input
              id="month"
              type="month"
              value={formData.month}
              onChange={(e) => setFormData({...formData, month: e.target.value})}
              data-testid="setup-month"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="truck">Truck *</Label>
            <Select 
              value={formData.truck_id} 
              onValueChange={(v) => setFormData({...formData, truck_id: v})}
            >
              <SelectTrigger data-testid="setup-truck-select">
                <SelectValue placeholder="Select a truck" />
              </SelectTrigger>
              <SelectContent>
                {trucks.map((truck) => (
                  <SelectItem key={truck.id} value={truck.id}>
                    {truck.registration_number} - {truck.make} {truck.model}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="trailer">Trailer (Optional)</Label>
            <Select 
              value={formData.trailer_id || 'none'} 
              onValueChange={(v) => setFormData({...formData, trailer_id: v === 'none' ? '' : v})}
            >
              <SelectTrigger data-testid="setup-trailer-select">
                <SelectValue placeholder="Select a trailer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Trailer</SelectItem>
                {allTrailers.map((trailer) => (
                  <SelectItem key={trailer.id} value={trailer.id}>
                    {trailer.registration_number} - {trailer.trailer_type || trailer.vehicle_type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="truck_tyre_count">Truck Tyre Count</Label>
              <Input
                id="truck_tyre_count"
                type="number"
                min="2"
                max="24"
                value={formData.truck_tyre_count}
                onChange={(e) => setFormData({...formData, truck_tyre_count: parseInt(e.target.value) || 0})}
                data-testid="setup-truck-tyre-count"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="trailer_tyre_count">Trailer Tyre Count</Label>
              <Input
                id="trailer_tyre_count"
                type="number"
                min="0"
                max="24"
                value={formData.trailer_tyre_count}
                onChange={(e) => setFormData({...formData, trailer_tyre_count: parseInt(e.target.value) || 0})}
                disabled={!formData.trailer_id}
                data-testid="setup-trailer-tyre-count"
              />
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} data-testid="setup-submit-btn">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {setup ? 'Update' : 'Create'} Setup
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Survey Form Dialog Component - Fixed to prevent input focus loss
const SurveyFormDialog = ({ open, onClose, setup, onSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [tyreData, setTyreData] = useState({});

  // Initialize tyre data when dialog opens
  useEffect(() => {
    if (open && setup) {
      const initialData = {};
      
      // Initialize truck tyres
      for (let i = 1; i <= setup.truck_tyre_count; i++) {
        const key = `truck_${i}`;
        initialData[key] = {
          position: i,
          tyre_type: 'truck',
          brand: '',
          size: '',
          serial_number: '',
          pressure_kpa: '',
          depth_mm: '',
          ply: '',
          valve: 'Good',
          valve_ext: 'Yes',
          even_wear: true,
          overall_condition: 'good',
          actions_required: '',
        };
      }
      
      // Initialize trailer tyres
      for (let i = 1; i <= setup.trailer_tyre_count; i++) {
        const key = `trailer_${i}`;
        initialData[key] = {
          position: i,
          tyre_type: 'trailer',
          brand: '',
          size: '',
          serial_number: '',
          pressure_kpa: '',
          depth_mm: '',
          ply: '',
          valve: 'Good',
          valve_ext: 'Yes',
          even_wear: true,
          overall_condition: 'good',
          actions_required: '',
        };
      }
      
      setTyreData(initialData);
      setNotes('');
    }
  }, [open, setup]);

  // Update a single field for a tyre - using functional update to avoid stale state
  const handleFieldChange = (tyreKey, field, value) => {
    setTyreData(prev => ({
      ...prev,
      [tyreKey]: {
        ...prev[tyreKey],
        [field]: value
      }
    }));
  };

  const handleSubmit = async () => {
    // Convert tyreData object to array
    const tyresArray = Object.values(tyreData).map(tyre => ({
      ...tyre,
      pressure_kpa: tyre.pressure_kpa === '' ? null : parseFloat(tyre.pressure_kpa),
      depth_mm: tyre.depth_mm === '' ? null : parseFloat(tyre.depth_mm),
    }));

    setLoading(true);
    try {
      await onSubmit({
        setup_id: setup.id,
        survey_date: new Date().toISOString(),
        tyres: tyresArray,
        notes: notes || null,
      });
      onClose();
    } catch (error) {
      console.error('Error submitting survey:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get tyre keys organized by type
  const truckKeys = Object.keys(tyreData).filter(k => k.startsWith('truck_')).sort((a, b) => {
    return parseInt(a.split('_')[1]) - parseInt(b.split('_')[1]);
  });
  const trailerKeys = Object.keys(tyreData).filter(k => k.startsWith('trailer_')).sort((a, b) => {
    return parseInt(a.split('_')[1]) - parseInt(b.split('_')[1]);
  });

  // Render a single tyre input section - uses stable keys
  const renderTyreInputs = (tyreKey) => {
    const tyre = tyreData[tyreKey];
    if (!tyre) return null;
    
    const posLabel = tyre.tyre_type === 'truck' ? `T${tyre.position}` : `TR${tyre.position}`;
    
    return (
      <div 
        key={tyreKey} 
        className="border rounded-sm p-3 space-y-3 bg-slate-50" 
        data-testid={`tyre-row-${tyreKey}`}
      >
        <div className="flex items-center justify-between">
          <Badge variant="outline" className="font-mono">{posLabel}</Badge>
          <span className="text-sm text-slate-500">Position {tyre.position}</span>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Brand</Label>
            <Input
              defaultValue={tyre.brand}
              onBlur={(e) => handleFieldChange(tyreKey, 'brand', e.target.value)}
              placeholder="Brand"
              className="h-8 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs">Size</Label>
            <Input
              defaultValue={tyre.size}
              onBlur={(e) => handleFieldChange(tyreKey, 'size', e.target.value)}
              placeholder="e.g., 315/80R22.5"
              className="h-8 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs">Serial #</Label>
            <Input
              defaultValue={tyre.serial_number}
              onBlur={(e) => handleFieldChange(tyreKey, 'serial_number', e.target.value)}
              placeholder="Serial"
              className="h-8 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs">PLY</Label>
            <Input
              defaultValue={tyre.ply}
              onBlur={(e) => handleFieldChange(tyreKey, 'ply', e.target.value)}
              placeholder="e.g., 18PR"
              className="h-8 text-sm"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Pressure (kPa) *</Label>
            <Input
              type="number"
              defaultValue={tyre.pressure_kpa}
              onBlur={(e) => handleFieldChange(tyreKey, 'pressure_kpa', e.target.value)}
              placeholder={tyre.tyre_type === 'truck' ? 'Min 800' : 'Min 700'}
              className="h-8 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs">Depth (mm) *</Label>
            <Input
              type="number"
              step="0.1"
              defaultValue={tyre.depth_mm}
              onBlur={(e) => handleFieldChange(tyreKey, 'depth_mm', e.target.value)}
              placeholder="Min 2mm"
              className="h-8 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs">Valve</Label>
            <Select 
              defaultValue={tyre.valve}
              onValueChange={(v) => handleFieldChange(tyreKey, 'valve', v)}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Good">Good</SelectItem>
                <SelectItem value="Fair">Fair</SelectItem>
                <SelectItem value="Poor">Poor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Valve Ext</Label>
            <Select 
              defaultValue={tyre.valve_ext}
              onValueChange={(v) => handleFieldChange(tyreKey, 'valve_ext', v)}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Even Wear</Label>
            <Select 
              defaultValue={tyre.even_wear ? 'yes' : 'no'}
              onValueChange={(v) => handleFieldChange(tyreKey, 'even_wear', v === 'yes')}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Overall Condition *</Label>
            <Select 
              defaultValue={tyre.overall_condition}
              onValueChange={(v) => handleFieldChange(tyreKey, 'overall_condition', v)}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="good">Good</SelectItem>
                <SelectItem value="fair">Fair</SelectItem>
                <SelectItem value="poor">Poor</SelectItem>
                <SelectItem value="replace">Replace</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-1">
            <Label className="text-xs">Actions Required</Label>
            <Input
              defaultValue={tyre.actions_required}
              onBlur={(e) => handleFieldChange(tyreKey, 'actions_required', e.target.value)}
              placeholder="Any actions needed"
              className="h-8 text-sm"
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5" />
            Tyre Survey
          </DialogTitle>
          <DialogDescription>
            {setup?.truck_registration}
            {setup?.trailer_registration && ` + ${setup.trailer_registration}`}
            {' • '}
            {setup?.truck_tyre_count + (setup?.trailer_tyre_count || 0)} tyres total
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Truck Tyres */}
          <div>
            <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Truck className="w-4 h-4" />
              Truck Tyres ({setup?.truck_registration})
            </h3>
            <div className="space-y-3">
              {truckKeys.map(key => renderTyreInputs(key))}
            </div>
          </div>
          
          {/* Trailer Tyres */}
          {trailerKeys.length > 0 && (
            <div>
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <CircleDot className="w-4 h-4" />
                Trailer Tyres ({setup?.trailer_registration})
              </h3>
              <div className="space-y-3">
                {trailerKeys.map(key => renderTyreInputs(key))}
              </div>
            </div>
          )}
          
          {/* Notes */}
          <div>
            <Label>Additional Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional observations or notes..."
              rows={3}
              data-testid="survey-notes"
            />
          </div>
        </div>
        
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} data-testid="survey-submit-btn">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Submit Survey
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Survey Detail/Edit Dialog Component
const SurveyDetailDialog = ({ open, onClose, survey, onUpdate, onRefresh }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedTyres, setEditedTyres] = useState([]);
  const [editedNotes, setEditedNotes] = useState('');

  useEffect(() => {
    if (survey) {
      setEditedTyres(survey.tyres || []);
      setEditedNotes(survey.notes || '');
      setIsEditing(false);
    }
  }, [survey]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(
        `${API_URL}/api/tyre-surveys/${survey.id}/upload-document`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success('Document uploaded successfully');
      onRefresh();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleExportExcel = () => {
    downloadFile(`${API_URL}/api/tyre-surveys/${survey.id}/export/excel`);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/tyre-surveys/${survey.id}`, {
        tyres: editedTyres,
        notes: editedNotes,
      });
      toast.success('Survey updated successfully');
      setIsEditing(false);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update survey');
    } finally {
      setSaving(false);
    }
  };

  const updateTyre = (index, field, value) => {
    setEditedTyres(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  if (!survey) return null;

  const truckTyres = (isEditing ? editedTyres : survey.tyres || []).filter(t => t.tyre_type === 'truck');
  const trailerTyres = (isEditing ? editedTyres : survey.tyres || []).filter(t => t.tyre_type === 'trailer');

  const getConditionBadge = (condition) => {
    const colors = {
      good: 'bg-emerald-100 text-emerald-700',
      fair: 'bg-amber-100 text-amber-700',
      poor: 'bg-red-100 text-red-700',
      replace: 'bg-red-200 text-red-800',
    };
    return colors[condition] || 'bg-slate-100 text-slate-700';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5" />
                Tyre Survey Details
              </DialogTitle>
              <DialogDescription>
                {survey.truck_registration}
                {survey.trailer_registration && ` + ${survey.trailer_registration}`}
                {' • '}
                {new Date(survey.survey_date).toLocaleDateString()}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportExcel}
                className="gap-1"
                data-testid="export-survey-btn"
              >
                <Download className="w-4 h-4" />
                Export Excel
              </Button>
              <label>
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  disabled={uploading}
                  asChild
                >
                  <span>
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    Upload Doc
                  </span>
                </Button>
              </label>
            </div>
          </div>
        </DialogHeader>

        {/* Survey Info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-sm">
          <div>
            <Label className="text-xs text-slate-500">Surveyed By</Label>
            <p className="font-medium">{survey.surveyed_by_name}</p>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Survey Date</Label>
            <p className="font-medium">{new Date(survey.survey_date).toLocaleDateString()}</p>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Tyres Inspected</Label>
            <p className="font-medium">{survey.tyres?.length || 0}</p>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Status</Label>
            {survey.has_issues ? (
              <Badge className="bg-orange-100 text-orange-700">
                {survey.corrective_action_ids?.length || 0} Issue(s)
              </Badge>
            ) : (
              <Badge className="bg-emerald-100 text-emerald-700">All Good</Badge>
            )}
          </div>
        </div>

        {/* Documents */}
        {survey.documents && survey.documents.length > 0 && (
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Attached Documents</Label>
            <div className="flex flex-wrap gap-2">
              {survey.documents.map((doc, idx) => (
                <a
                  key={idx}
                  href={`${API_URL}/api/uploads/tyre_surveys/${doc}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-sm text-sm hover:bg-blue-100"
                >
                  <Paperclip className="w-3 h-3" />
                  Document {idx + 1}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Tyre Data Table */}
        <div className="space-y-4">
          {/* Truck Tyres */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Truck Tyres ({survey.truck_registration})
              </h3>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Pos</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Serial #</TableHead>
                  <TableHead>Pressure</TableHead>
                  <TableHead>Depth</TableHead>
                  <TableHead>Condition</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {truckTyres.map((tyre, idx) => (
                  <TableRow key={`truck-${idx}`}>
                    <TableCell className="font-mono font-bold">T{tyre.position}</TableCell>
                    <TableCell>{tyre.brand || '-'}</TableCell>
                    <TableCell>{tyre.size || '-'}</TableCell>
                    <TableCell className="font-mono text-xs">{tyre.serial_number || '-'}</TableCell>
                    <TableCell>
                      <span className={tyre.pressure_kpa < 800 ? 'text-red-600 font-bold' : ''}>
                        {tyre.pressure_kpa || '-'} kPa
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={tyre.depth_mm < 2 ? 'text-red-600 font-bold' : ''}>
                        {tyre.depth_mm || '-'} mm
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={getConditionBadge(tyre.overall_condition)}>
                        {tyre.overall_condition}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Trailer Tyres */}
          {trailerTyres.length > 0 && (
            <div>
              <h3 className="font-semibold flex items-center gap-2 mb-2">
                <CircleDot className="w-4 h-4" />
                Trailer Tyres ({survey.trailer_registration})
              </h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Pos</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Serial #</TableHead>
                    <TableHead>Pressure</TableHead>
                    <TableHead>Depth</TableHead>
                    <TableHead>Condition</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trailerTyres.map((tyre, idx) => (
                    <TableRow key={`trailer-${idx}`}>
                      <TableCell className="font-mono font-bold">TR{tyre.position}</TableCell>
                      <TableCell>{tyre.brand || '-'}</TableCell>
                      <TableCell>{tyre.size || '-'}</TableCell>
                      <TableCell className="font-mono text-xs">{tyre.serial_number || '-'}</TableCell>
                      <TableCell>
                        <span className={tyre.pressure_kpa < 700 ? 'text-red-600 font-bold' : ''}>
                          {tyre.pressure_kpa || '-'} kPa
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={tyre.depth_mm < 2 ? 'text-red-600 font-bold' : ''}>
                          {tyre.depth_mm || '-'} mm
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={getConditionBadge(tyre.overall_condition)}>
                          {tyre.overall_condition}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Notes */}
        {(survey.notes || isEditing) && (
          <div>
            <Label className="text-sm font-semibold">Notes</Label>
            {isEditing ? (
              <Textarea
                value={editedNotes}
                onChange={(e) => setEditedNotes(e.target.value)}
                rows={3}
              />
            ) : (
              <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-sm mt-1">
                {survey.notes || 'No notes'}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel Edit</Button>
              <Button onClick={handleSaveEdit} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => setIsEditing(true)}>
              <Edit className="w-4 h-4 mr-2" />
              Edit Survey
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Page Component
export const TyreSurveyPage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'setups');
  
  // Data states
  const [setups, setSetups] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [monthStatus, setMonthStatus] = useState(null);
  
  // Dialog states
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [surveyDialogOpen, setSurveyDialogOpen] = useState(false);
  const [surveyDetailOpen, setSurveyDetailOpen] = useState(false);
  const [selectedSetup, setSelectedSetup] = useState(null);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [editingSetup, setEditingSetup] = useState(null);
  
  // Filter states
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());

  // Handle tab from URL
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'surveys') {
      setActiveTab('surveys');
    }
  }, [searchParams]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [setupsRes, surveysRes, vehiclesRes, trailersRes, statusRes] = await Promise.all([
        tyreSurveyAPI.listSetups({ month: selectedMonth }),
        tyreSurveyAPI.listSurveys({ month: selectedMonth }),
        vehiclesAPI.list(),
        trailersAPI.list(),
        tyreSurveyAPI.getCurrentMonthStatus(),
      ]);
      
      setSetups(setupsRes.data || []);
      setSurveys(surveysRes.data || []);
      setVehicles(vehiclesRes.data || []);
      setTrailers(trailersRes.data || []);
      setMonthStatus(statusRes.data || null);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load tyre survey data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  const handleCreateSetup = async (data) => {
    try {
      if (editingSetup) {
        await tyreSurveyAPI.updateSetup(editingSetup.id, data);
        toast.success('Setup updated successfully');
      } else {
        await tyreSurveyAPI.createSetup(data);
        toast.success('Setup created successfully');
      }
      setEditingSetup(null);
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save setup';
      toast.error(msg);
      throw error;
    }
  };

  const handleDeleteSetup = async (setupId) => {
    if (!window.confirm('Are you sure you want to delete this setup?')) return;
    try {
      await tyreSurveyAPI.deleteSetup(setupId);
      toast.success('Setup deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete setup');
    }
  };

  const handleStartSurvey = (setup) => {
    setSelectedSetup(setup);
    setSurveyDialogOpen(true);
  };

  const handleSubmitSurvey = async (data) => {
    try {
      const result = await tyreSurveyAPI.createSurvey(
        data, 
        user.id, 
        `${user.first_name} ${user.last_name}`
      );
      
      if (result.data.has_issues) {
        toast.warning(
          `Survey submitted with ${result.data.corrective_actions_created} issue(s) found. Corrective actions created.`,
          { duration: 5000 }
        );
      } else {
        toast.success('Survey submitted successfully - no issues found');
      }
      
      setSelectedSetup(null);
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to submit survey';
      toast.error(msg);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tyre-survey-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            TYRE SURVEY
          </h1>
          <p className="text-slate-500 mt-1">
            Monthly tyre inspections and condition tracking
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-40"
            data-testid="month-filter"
          />
          <Button 
            onClick={() => {
              setEditingSetup(null);
              setSetupDialogOpen(true);
            }}
            className="gap-2"
            data-testid="new-setup-btn"
          >
            <Plus className="w-4 h-4" />
            New Setup
          </Button>
        </div>
      </div>

      {/* Alert: No setups configured for current month */}
      {monthStatus && monthStatus.total_setups === 0 && selectedMonth === getCurrentMonth() && (
        <Card className="border-l-4 border-l-red-500 bg-red-50" data-testid="no-setup-alert">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-800">
                  Action Required: No Tyre Setups Configured
                </h3>
                <p className="text-sm text-red-700 mt-1">
                  Monthly tyre survey setups have not been created for {formatMonth(selectedMonth)}. 
                  Please configure which trucks/trailers need to be surveyed this month.
                </p>
              </div>
              <Button 
                onClick={() => setSetupDialogOpen(true)}
                className="bg-red-600 hover:bg-red-700"
              >
                <Plus className="w-4 h-4 mr-1" />
                Create Setup Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alert: Pending surveys reminder */}
      {monthStatus && monthStatus.pending_setups && monthStatus.pending_setups.length > 0 && selectedMonth === getCurrentMonth() && (
        <Card className="border-l-4 border-l-amber-500 bg-amber-50" data-testid="pending-surveys-alert">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800">
                  Pending Tyre Surveys ({monthStatus.pending_setups.length})
                </h3>
                <p className="text-sm text-amber-700 mt-1 mb-2">
                  The following vehicles have not been surveyed this month:
                </p>
                <div className="flex flex-wrap gap-2">
                  {monthStatus.pending_setups.map((setup) => (
                    <Badge 
                      key={setup.id} 
                      variant="outline" 
                      className="bg-white border-amber-300 text-amber-800 cursor-pointer hover:bg-amber-100"
                      onClick={() => {
                        // Find the setup and start survey
                        const fullSetup = setups.find(s => s.id === setup.id);
                        if (fullSetup) {
                          setSelectedSetup(fullSetup);
                          setSurveyDialogOpen(true);
                        }
                      }}
                    >
                      {setup.truck_registration}
                      {setup.trailer_registration && ` + ${setup.trailer_registration}`}
                      <span className="ml-1 text-amber-600">({setup.tyre_count} tyres)</span>
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Card */}
      {monthStatus && (
        <Card className="border-l-4 border-l-blue-500" data-testid="month-status-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-sm flex items-center justify-center">
                  <CircleDot className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">
                    {formatMonth(monthStatus.month)} Survey Progress
                  </h3>
                  <p className="text-sm text-slate-500">
                    {monthStatus.surveyed_setups} of {monthStatus.total_setups} setups surveyed
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-blue-600 font-mono">
                  {monthStatus.percentage}%
                </div>
                <p className="text-sm text-slate-500">
                  {monthStatus.surveyed_tyres} / {monthStatus.total_tyres} tyres
                </p>
              </div>
            </div>
            
            {/* Progress bar */}
            <div className="mt-4 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-500 transition-all"
                style={{ width: `${monthStatus.percentage}%` }}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="setups" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            Monthly Setups ({setups.length})
          </TabsTrigger>
          <TabsTrigger value="surveys" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            Survey History ({surveys.length})
          </TabsTrigger>
        </TabsList>
        
        {/* Export Buttons */}
        {activeTab === 'setups' && (
          <div className="float-right flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => downloadFile(`${API_URL}/api/tyre-surveys/setups/export/excel?month=${selectedMonth}`)}
              data-testid="export-setups-month-btn"
            >
              <Download className="w-4 h-4" />
              Export {formatMonth(selectedMonth)} Schedule
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => downloadFile(`${API_URL}/api/tyre-surveys/setups/export/excel`)}
              data-testid="export-all-setups-btn"
            >
              <Download className="w-4 h-4" />
              Export All Schedules
            </Button>
          </div>
        )}

        {/* Export Buttons for Survey History */}
        {activeTab === 'surveys' && surveys.length > 0 && (
          <div className="float-right flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => downloadFile(`${API_URL}/api/tyre-surveys/export/all?month=${selectedMonth}`)}
              data-testid="export-month-surveys-btn"
            >
              <Download className="w-4 h-4" />
              Export {formatMonth(selectedMonth)}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => downloadFile(`${API_URL}/api/tyre-surveys/export/all?all_history=true`)}
              data-testid="export-all-history-btn"
            >
              <Download className="w-4 h-4" />
              Export Full History
            </Button>
          </div>
        )}

        {/* Setups Tab */}
        <TabsContent value="setups" className="mt-4">
          {setups.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <CircleDot className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">No setups for {formatMonth(selectedMonth)}</h3>
                <p className="text-slate-500 mt-1 mb-4">Create a setup to start surveying tyres</p>
                <Button onClick={() => setSetupDialogOpen(true)} data-testid="create-first-setup-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Setup
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {setups.map((setup) => {
                const hasSurvey = setup.last_survey_date && setup.last_survey_date.startsWith(selectedMonth);
                return (
                  <Card 
                    key={setup.id} 
                    className={`hover:shadow-md transition-all ${hasSurvey ? 'border-l-4 border-l-emerald-500' : ''}`}
                    data-testid={`setup-card-${setup.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Truck className="w-5 h-5 text-slate-600" />
                            <span className="font-mono font-bold text-slate-900">
                              {setup.truck_registration}
                            </span>
                            {setup.trailer_registration && (
                              <>
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                                <span className="font-mono text-slate-600">
                                  {setup.trailer_registration}
                                </span>
                              </>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-slate-500">
                            <span>{setup.truck_tyre_count} truck tyres</span>
                            {setup.trailer_tyre_count > 0 && (
                              <span>{setup.trailer_tyre_count} trailer tyres</span>
                            )}
                          </div>
                          
                          <div className="mt-3">
                            {hasSurvey ? (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Surveyed
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Pending
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-col gap-2">
                          <Button 
                            size="sm" 
                            onClick={() => handleStartSurvey(setup)}
                            data-testid={`start-survey-${setup.id}`}
                          >
                            <ClipboardList className="w-4 h-4 mr-1" />
                            {hasSurvey ? 'New Survey' : 'Start Survey'}
                          </Button>
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => {
                                setEditingSetup(setup);
                                setSetupDialogOpen(true);
                              }}
                              data-testid={`edit-setup-${setup.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleDeleteSetup(setup.id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-setup-${setup.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Surveys Tab */}
        <TabsContent value="surveys" className="mt-4">
          {surveys.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">No surveys for {formatMonth(selectedMonth)}</h3>
                <p className="text-slate-500 mt-1">Complete a survey from the Monthly Setups tab</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {surveys.map((survey) => (
                <Card 
                  key={survey.id}
                  className={`hover:shadow-md transition-all cursor-pointer ${survey.has_issues ? 'border-l-4 border-l-orange-500' : 'border-l-4 border-l-emerald-500'}`}
                  data-testid={`survey-card-${survey.id}`}
                  onClick={() => {
                    setSelectedSurvey(survey);
                    setSurveyDetailOpen(true);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono font-bold text-slate-900">
                            {survey.truck_registration}
                          </span>
                          {survey.trailer_registration && (
                            <>
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                              <span className="font-mono text-slate-600">
                                {survey.trailer_registration}
                              </span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span>
                            <Calendar className="w-4 h-4 inline mr-1" />
                            {new Date(survey.survey_date).toLocaleDateString()}
                          </span>
                          <span>By: {survey.surveyed_by_name}</span>
                          <span>{survey.tyres?.length || 0} tyres inspected</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {survey.has_issues ? (
                          <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {survey.corrective_action_ids?.length || 0} Issue(s)
                          </Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            All Good
                          </Badge>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSurvey(survey);
                            setSurveyDetailOpen(true);
                          }}
                          data-testid={`view-survey-${survey.id}`}
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Setup Dialog */}
      <SetupDialog
        open={setupDialogOpen}
        onClose={() => {
          setSetupDialogOpen(false);
          setEditingSetup(null);
        }}
        onSubmit={handleCreateSetup}
        setup={editingSetup}
        vehicles={vehicles}
        trailers={trailers}
      />

      {/* Survey Form Dialog */}
      <SurveyFormDialog
        open={surveyDialogOpen}
        onClose={() => {
          setSurveyDialogOpen(false);
          setSelectedSetup(null);
        }}
        setup={selectedSetup}
        onSubmit={handleSubmitSurvey}
      />

      {/* Survey Detail Dialog */}
      <SurveyDetailDialog
        open={surveyDetailOpen}
        onClose={() => {
          setSurveyDetailOpen(false);
          setSelectedSurvey(null);
        }}
        survey={selectedSurvey}
        onRefresh={fetchData}
      />
    </div>
  );
};

export default TyreSurveyPage;
